import {
  isSentinel,
  sentinelLabel,
  formatCurrency,
  formatPercent,
  enumLabel,
  formatBenefitAmount,
  formatDefinitionOfEarnings,
  DEFINITION_OF_DISABILITY_LABELS,
  EMPLOYER_CONTRIBUTION_LABELS,
  INCLUSION_LABELS,
  formatRateGuarantee,
  formatPreExistingConditionMonthsDetail,
} from "./shared";

export type StdFieldFormatter = (row: Record<string, unknown>) => string;

/**
 * Maximum Amount — dollarAmount → $X; percentage cap → X% with optional $Y cap;
 * unlimited → "Unlimited".
 */
const formatMaximumAmount: StdFieldFormatter = (row) => {
  const type = row["maximumAmountType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "unlimited") return "Unlimited";
  if (type === "dollarAmount")
    return formatCurrency(row["maximumAmountDollarAmount"]) || "";
  if (type === "dollarAmountNotToExceedPercentageOfPredisabilityEarnings") {
    const dollar = formatCurrency(row["maximumAmountDollarAmount"]);
    const pct = formatPercent(row["maximumAmountPercent"]);
    if (dollar && pct) return `Lesser of ${dollar} or ${pct} earnings`;
    return dollar || pct || "";
  }
  return "";
};

/** Elimination period — days → "X days"; notIncluded → "Not Included". */
const eliminationPeriod =
  (typeKey: string, daysKey: string): StdFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);

    if (type === "days") {
      const days = row[daysKey];
      return days != null ? `${String(days)} days` : "";
    }
    return "";
  };

/** Benefit duration — days/weeks → "X days/weeks"; notIncluded → "Not Included". */
const formatBenefitDuration: StdFieldFormatter = (row) => {
  const type = row["benefitDurationType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  const amount = row["benefitDurationAmount"];
  if (type === "days") return amount != null ? `${String(amount)} days` : "";
  if (type === "weeks") return amount != null ? `${String(amount)} weeks` : "";
  return "";
};

/**
 * Minimum Benefit (CD-2552) — dollarAmount → $X, percentageOfSalary → X%,
 * sentinels (notDisclosed) pass through. Same pattern as Critical Illness's
 * condition riders (CD-2325).
 */
const formatMinimumBenefit: StdFieldFormatter = (row) => {
  const type = row["minimumBenefitType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "percentageOfSalary")
    return formatPercent(row["minimumBenefitAmount"]) || "";
  if (type === "dollarAmount")
    return formatCurrency(row["minimumBenefitAmount"]) || "";
  return "";
};

/** Pre-existing condition — same pattern as cancer/hospital/CI. */
const formatPreExistingCondition: StdFieldFormatter = (row) => {
  const type = row["preExistingConditionType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "waived") return "Waived";
  if (type === "none") return "None";
  if (type !== "months") return "";
  const detail = row["preExistingConditionMonthsDetail"];
  return typeof detail === "string" && detail
    ? formatPreExistingConditionMonthsDetail(detail)
    : "";
};

const BILLABLE_TYPE_LABELS: Record<string, string> = {
  billable: "Billable",
  nonBillable: "Non-Billable",
  notApplicable: "Not Applicable",
  notIncluded: "Not Included",
};

export const stdFormatters: Record<string, StdFieldFormatter> = {
  benefitAmount: formatBenefitAmount,
  maximumAmount: formatMaximumAmount,
  eliminationPeriodAccident: eliminationPeriod(
    "eliminationPeriodAccidentType",
    "eliminationPeriodAccidentDays",
  ),
  eliminationPeriodIllness: eliminationPeriod(
    "eliminationPeriodIllnessType",
    "eliminationPeriodIllnessDays",
  ),
  benefitDuration: formatBenefitDuration,
  minimumBenefit: formatMinimumBenefit,
  preExistingCondition: formatPreExistingCondition,
  definitionOfEarnings: formatDefinitionOfEarnings,
  definitionOfDisability: enumLabel(
    "definitionOfDisability",
    DEFINITION_OF_DISABILITY_LABELS,
  ),
  employerContribution: enumLabel(
    "employerContribution",
    EMPLOYER_CONTRIBUTION_LABELS,
  ),
  billableType: enumLabel("billableType", BILLABLE_TYPE_LABELS),
  ficaMatch: enumLabel("ficaMatch", INCLUSION_LABELS),
  rateGuarantee: formatRateGuarantee,
};

export const formatStdField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = stdFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
